<script setup>
import { phase, projectStatus, type } from '@/datas/projects'
import LogoutTime from '@/layouts/components/LogoutTime.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'
import { useSnackBar } from '@/utils/stores/snackbar'
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'blank',
    action: 'edit',
    subject: 'project',
    navActiveLink: 'projects',
  },
})

const route = useRoute()
const router = useRouter()
const snackbarStore = useSnackBar()

const isLoading = ref(false)
const projectId = route.params.id

const data = reactive({
  projectSummary: '',
  projectName: '',
  projectNo: '',
  projectDesc: '',
  sponsor: '',
  type: '동물용 의약품',
  typeEtc: '',            // ✅ 기타일 때 직접 입력
  phase: '10',
  phaseEtc: '',           // ✅ phase = 0일 때 직접 입력
  startDate: '',
  isNewProject: false,

  targetCount: '',
  hasRandomization: true,
  hasGuardianSurvey: true,
  hasRtms: false,
  projectColor: 'gray',
})

const refForm = ref(null)
const typeRadios = type
const phaseRadios = phase
const projectStatusRadios = projectStatus

// ✅ 기타/기타-Phase 여부
const isTypeEtc = computed(() => {
  const v = String(data.type ?? '').trim()
  
  return v === '기타' || v.toLowerCase() === 'etc'
})

const isPhaseEtc = computed(() => Number(data.phase ?? 0) === 0)

// ✅ 수정 모드: 데이터 로드
const loadProject = async () => {
  isLoading.value = true
  try {
    const result = await $api(`/projects/${projectId}`, { method: 'GET' })

    if (result.locked) {
      snackbarStore.showSnackBar('Locked Project', 'error')
      router.back()
      
      return
    }

    Object.assign(data, {
      projectName: result.project_name ?? result.projectName ?? '',
      projectSummary: result.project_summary ?? result.projectSummary ?? '',
      projectNo: result.project_no ?? result.projectNo ?? '',
      projectDesc: result.project_desc ?? result.projectDesc ?? '',
      projectStatus: result.project_status ?? result.projectStatus ?? '',
      sponsor: result.sponsor ?? '',
      type: result.type ?? '동물용 의약품',
      typeEtc: result.type_etc ?? result.typeEtc ?? '',        // ✅ 로드
      phase: String(result.phase ?? '10'),
      phaseEtc: result.phase_etc ?? result.phaseEtc ?? '',     // ✅ 로드
      startDate: result.start_date ? new Date(result.start_date) : '',

 
      targetCount: result.target_subjects?? result.targetSubjects ?? '',   
      isNewProject: (result.project_v ?? '') === '2',
      hasRandomization: !!(result.hasRandomization ?? result.has_randomization ?? data.hasRandomization),
      hasGuardianSurvey: !!(result.hasGuardianSurvey ?? result.has_guardian_survey ?? data.hasGuardianSurvey),
      hasRtms: !!(result.hasRtms ?? result.has_rtms ?? data.hasRtms),
      projectColor: (result.projectColor ?? result.project_color ?? data.projectColor) || 'gray', // ✅ 문자열 유지
    })
  } catch (error) {
    console.log(error)
    snackbarStore.showSnackBar('Failed to load project data', 'error')
    router.back()
  } finally {
    isLoading.value = false
  }
}

// ✅ 저장(수정)
const saveProject = async () => {
  const validate = await refForm?.value.validate()
  if (!validate.valid) return

  // 선택이 '기타'가 아니면 입력값 비워서 보냄(서버에서 무시/NULL 처리 가능)
  const typeEtcValue = isTypeEtc.value ? (data.typeEtc || '') : null
  const phaseEtcValue = isPhaseEtc.value ? (data.phaseEtc || '') : null

  // 날짜 문자열로 정규화(YYYY-MM-DD)
  const startDateStr = data.startDate
    ? new Date(data.startDate).toISOString().slice(0, 10)
    : null

  const payload = {
    // snake_case 
    sponsor: data.sponsor,
    type: data.type, 
    phase: Number(data.phase), 

    // camelCase(백엔드에서 camel을 기대할 수도 있을 때 대비)
    projectName: data.projectName,
    projectSummary: data.projectSummary,
    projectNo: data.projectNo,
    projectDesc: data.projectDesc,
    targetCount: data.targetCount,
    typeEtc: typeEtcValue,
    phaseEtc: phaseEtcValue,
    startDate: startDateStr,
    projectStatus: data.projectStatus,
    hasRandomization: !!data.hasRandomization,
    hasGuardianSurvey: !!data.hasGuardianSurvey,
    hasRtms: !!data.hasRtms,
    projectColor: data.projectColor,
  }

  try {
    await $api(`/projects/${projectId}`, {
      method: 'PUT',
      body: { project: payload },
    })
    snackbarStore.showSnackBar('Successfully Updated!')
    router.push({ name: 'projects' })
  } catch (e) {
    snackbarStore.showSnackBar('Update failed', 'error')
  }
}

onMounted(() => {
  if (projectId) loadProject()
})
</script>

<template>
  <!-- 상단 바 -->
  <VCard
    class="topbar-card"
    style="border-radius:0;"
  >
    <VCardText class="d-flex justify-space-between align-between py-2">
      <!-- 👇 로고 버튼 (왼쪽) -->
      <div
        class="d-flex align-center cursor-pointer"
        @click="router.push('/projects')"
      >
        <img
          src="/images/home_logo.png"
          alt="홈"
          width="87"
          height="30"
          style="display:block"
        >
      </div>

      <!-- 오른쪽 기존 요소 -->
      <div class="d-flex align-center">
        <LogoutTime class="me-2" />
        <UserProfile />
        <div class="divider-vert" />
        <div
          class="mgmt-quick d-flex align-center px-2 py-1"
          :style="{ backgroundColor: hover ? 'rgba(0,0,0,0.05)' : 'transparent' }"
          @click="router.push('/manage/permissions')"
          @mouseover="hover = true"
          @mouseleave="hover = false"
        >
          <img
            src="/images/settings.png"
            alt="관리"
            class="icon-20 me-1"
          >
          <span class="text-gray-600">관리</span>
        </div>
      </div>
    </VCardText>
  </VCard>

  <VRow class="mx-10">
    <VCol
      class="mx-auto"
      style="margin-block-start: 50px"
      cols="12"
      sm="12"
      md="9"
      lg="7"
      order="1"
      order-lg="1"
    >
      <!-- 폼 카드 -->
      <VCard
        class="form-card"
        title="시험 수정"
      >
        <VCardText class="mt-4">
          <VForm
            ref="refForm"
            @submit.prevent="() => {}"
          >
            <VRow>
              <!-- Project Summary -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectSummary"
                    >
                      프로젝트 명<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectSummary"
                      v-model="data.projectSummary"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Project Name -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectName"
                    >
                      {{ $t('Project Name') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectName"
                      v-model="data.projectName"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Project No -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectNo"
                    >
                      {{ $t('Project No') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectNo"
                      v-model="data.projectNo"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Project Desc -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectDesc"
                    >
                      {{ $t('Project Description') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectDesc"
                      v-model="data.projectDesc"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Sponsor -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="sponsor"
                    >
                      {{ $t('Sponsor') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="sponsor"
                      v-model="data.sponsor"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Type -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="type"
                    >
                      {{ $t('Type') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VRadioGroup v-model="data.type">
                      <VRadio
                        v-for="(item, index) in typeRadios"
                        :key="index"
                        :value="item.value"
                        :label="item.text"
                        density="compact"
                      />
                    </VRadioGroup>

                    <!-- ✅ type = 기타일 때만 노출 -->
                    <AppTextField
                      v-if="isTypeEtc"
                      v-model="data.typeEtc"
                      placeholder="기타 유형을 입력하세요"
                      :rules="[requiredValidator]"
                      hide-details="auto"
                      density="comfortable"
                      class="mt-2"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Phase -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="phase"
                    >
                      {{ $t('Phase') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VRadioGroup v-model="data.phase">
                      <VRadio
                        v-for="(item, index) in phaseRadios"
                        :key="index"
                        :value="item.value"
                        :label="item.text"
                        density="compact"
                      />
                    </VRadioGroup>

                    <!-- ✅ phase = 0일 때만 노출 -->
                    <AppTextField
                      v-if="isPhaseEtc"
                      v-model="data.phaseEtc"
                      placeholder="기타 Phase를 입력하세요"
                      :rules="[requiredValidator]"
                      hide-details="auto"
                      density="comfortable"
                      class="mt-2"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Project Status -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="project_status"
                    >
                      {{ $t('ProjectStatus') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VRadioGroup v-model="data.projectStatus">
                      <VRadio
                        v-for="(item, index) in projectStatusRadios"
                        :key="index"
                        :value="item.value"
                        :label="item.text"
                        density="compact"
                      />
                    </VRadioGroup>
                  </VCol>
                </VRow>
              </VCol>

              <!-- 무작위 배정 여부 -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label class="v-label text-body-2 text-high-emphasis">무작위 배정 여부</label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VSwitch
                      v-model="data.hasRandomization"
                      inset
                      :label="data.hasRandomization ? '사용' : '미사용'"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- 보호자 설문 여부 -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label class="v-label text-body-2 text-high-emphasis">보호자 설문 여부</label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VSwitch
                      v-model="data.hasGuardianSurvey"
                      inset
                      :label="data.hasGuardianSurvey ? '사용' : '미사용'"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- 시험약 관리(RTMS) 여부 -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label class="v-label text-body-2 text-high-emphasis">시험약 관리 여부</label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VSwitch
                      v-model="data.hasRtms"
                      inset
                      :label="data.hasRtms ? '사용' : '미사용'"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- 프로젝트 컬러 -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="6"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectColor"
                    >
                      프로젝트 컬러
                    </label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VSelect
                      v-model="data.projectColor"
                      :items="[
                        { text: '회색', value: 'gray', color: '#F2F4F6' },
                        { text: '빨강', value: 'red', color: '#ED9797' },
                        { text: '주황', value: 'orange', color: '#E5AB91' },
                        { text: '노랑', value: 'yellow', color: '#E4C8A0' },
                        { text: '초록', value: 'green', color: '#34AA5C' },
                        { text: '파랑', value: 'blue', color: '#7FABDD' },
                        { text: '보라', value: 'purple', color: '#C3A3E5' },
                      ]"
                      item-title="text"
                      item-value="value"
                      density="comfortable"
                      hide-details
                    >
                      <template #item="{ props, item }">
                        <VListItem v-bind="props">
                          <template #prepend>
                            <div
                              :style="{
                                backgroundColor: item.raw.color,
                                width: '16px',
                                height: '16px',
                                borderRadius: '4px',
                                marginRight: '8px'
                              }"
                            />
                          </template>
                        </VListItem>
                      </template>

                      <template #selection="{ item }">
                        <div class="d-flex align-center">
                          <div
                            :style="{
                              backgroundColor: item.raw.color,
                              width: '16px',
                              height: '16px',
                              borderRadius: '4px',
                              marginRight: '8px'
                            }"
                          />
                          {{ item.raw.text }}
                        </div>
                      </template>
                    </VSelect>
                  </VCol>
                </VRow>
              </VCol>
 

              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="targetCount"
                    >
                      목표 등록수<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectDesc"
                      v-model="data.targetCount"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- 버튼 -->
              <VCol
                offset-md="3"
                cols="12"
                md="9"
                class="d-flex gap-4"
              >
                <VBtn
                  type="submit"
                  @click="saveProject"
                >
                  {{ $t('Save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  @click="$router.back()"
                >
                  {{ $t('Cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped>
/* 폼 카드 톤 정리 */
:deep(.v-card.form-card) {
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(16, 24, 40, 6%);
}

/* 라벨 가독성 */
.v-label.text-body-2.text-high-emphasis {
  display: inline-flex;
  align-items: center;
  color: #1f2937;
  font-weight: 600;
  gap: 6px;
}

/* 입력 높이 적당히 */
:deep(.v-input) { --v-input-control-height: 44px; }

/* 라디오/스위치 상단 여백 조정 */
:deep(.v-radio-group), :deep(.v-switch) { margin-block-start: 4px; }

.icon-20{ block-size:20px; inline-size:20px; }

/* 버튼 간격 */
.d-flex.gap-4 { gap: 12px; }
</style>
